/**
 * Anagram browser-source entrypoint (continuous-play variant).
 *
 * URL params:
 *   key  — API key (required for non-mock mode)
 *   ws   — WS port (default 3001) for local dev
 *   mock=1 — skip WS and run a scripted demo for visual review
 */

import type { LbRow, OutMsg } from '@sg/shared';
import { readViewerMode, wsUrlFor } from '@sg/shared';
import { WsClient } from './ws/client.js';
import { resolveWsBase } from './ws/ws-base.js';
import { Store } from './state/store.js';
import type { SceneName } from './state/types.js';
import { createDomStage } from './scene/lib/stage.js';
import { applyTheme, DEFAULT_THEME } from './scene/lib/theme.js';
import { createLobby } from './scene/lobby.js';
import { createRound } from './scene/round.js';
import { createReveal } from './scene/reveal.js';
import { createPodium } from './scene/podium.js';
import { createConnectionLost } from './scene/connection-lost.js';
import { audioBus } from './audio/bus.js';
import { sfx as sfxImpl } from './audio/sfx.js';
import { music as musicImpl } from './audio/music.js';
import { mountAudioUnlock } from './ui/audio-unlock.js';
import { mountFullscreenButton } from './ui/fullscreen-button.js';
import { createAnimatedBg } from './ui/animated-bg.js';
import { createSideLeaderboard } from './ui/side-leaderboard.js';
import { createRoundSplash } from './ui/round-splash.js';
import { createConflictBanner } from './ui/conflict-banner.js';
import { mountWatchQrWidget } from '../../../shared/qrcode-generator/watch-qr.js';

/** Slug of THIS browser source — used to filter `cross_game_conflict` events. */
const MY_SLUG = 'anagram';

applyTheme(DEFAULT_THEME);

const params = new URL(window.location.href).searchParams;
const mockMode = params.get('mock') === '1';
// Viewer mode (`?viewer=1&handle=<HANDLE>`) routes through the public read-only
// /watch/<handle>/feed socket and suppresses streamer-only chrome.
const viewerMode = readViewerMode(params);
const isViewer = viewerMode.kind === 'viewer';
const key = viewerMode.kind === 'streamer' ? viewerMode.key : 'dev-no-key';
const wsPort = Number(params.get('ws') ?? '3001');

// Viewer-aware audio facades — silent no-ops in viewer mode so the AudioContext
// never instantiates. See platform/shared/src/viewer-mode.ts for the pattern.
const sfx: typeof sfxImpl = isViewer
  ? ({
      preload: async () => {},
      chime: () => {},
      ding: () => {},
      reveal: () => {},
      hint: () => {},
      tick: () => {},
    } as unknown as typeof sfxImpl)
  : sfxImpl;
const music: typeof musicImpl = isViewer
  ? ({ play: () => {}, stop: () => {} } as unknown as typeof musicImpl)
  : musicImpl;

const root = document.getElementById('root')!;
root.innerHTML = '';
root.style.cssText = 'position: relative; width: 100vw; height: 100vh; overflow: hidden;';
root.setAttribute('data-testid', 'browser-root');

const stage = createDomStage({ mount: root });
const stageEl = stage.el;

// Animated background sits behind every scene.
createAnimatedBg(stageEl);

const lobby = createLobby(stageEl);
const round = createRound(stageEl);
const reveal = createReveal(stageEl);
const podium = createPodium(stageEl);
const connectionLost = createConnectionLost(stageEl);
const sideLeaderboard = createSideLeaderboard(stageEl);
const roundSplash = createRoundSplash(stageEl);
// Cross-game conflict banner — backend fans `cross_game_conflict` to every
// connected browser source for the tenant; we filter by MY_SLUG below.
const conflictBanner = createConflictBanner(stageEl);
if (!isViewer) {
  mountFullscreenButton();
  mountAudioUnlock();
}

// Side leaderboard is hidden in lobby/podium and shown during round/reveal.
sideLeaderboard.setVisible(false);

if (!isViewer) {
  // Audio: pre-fetch + decode SFX buffers; lobby bed starts immediately so OBS
  // page-permissions kick it off without a gesture.
  void sfx.preload();
  music.play('lobby');
}

const allScenes: Record<SceneName, { setVisible(v: boolean): void }> = {
  lobby,
  round,
  reveal,
  podium,
  connection_lost: connectionLost,
};

function applyScene(next: SceneName): void {
  for (const [name, h] of Object.entries(allScenes)) {
    h.setVisible(name === next);
  }
  root.setAttribute('data-scene', next);
  (window as unknown as { __an_scene?: SceneName }).__an_scene = next;
  // Side leaderboard visible during round + reveal — both keep the right
  // panel populated. Hide on lobby/podium where it would compete with the
  // centered hero content.
  sideLeaderboard.setVisible(next === 'round' || next === 'reveal');
  // Music routing: lobby bed in lobby; round bed during play; silent on
  // reveal/podium so the SFX cues (ding, fanfare/chime) sit cleanly.
  if (next === 'lobby') {
    music.play('lobby');
  } else if (next === 'round') {
    music.play('round');
  } else {
    music.stop();
  }
}

const store = new Store();
applyScene(store.getState().scene);

let lastRoundIndex = -1;
// Drives sfx.tick() in the final 5s of each round.
let roundEndsAt = 0;
let lastRoundTickSecond = -1;

/**
 * After timer expiry/skip the backend emits `an_round_end`. We:
 *   1. Cascade-reveal the remaining tiles on the ROUND scene (~1.4s for 7
 *      letters; ~200ms stagger per letter).
 *   2. Pause 800ms so the answer lands visually.
 *   3. Dispatch the WS frame so the reducer transitions to the REVEAL scene.
 *
 * Tunable via the constants below; total budget ~2.5-3s before final screen.
 */
const CASCADE_PAD_MS = 800;

let watchQrCtrl: ReturnType<typeof mountWatchQrWidget> | null = null;
let showWatchQr = true;
let currentHandle: string | null = null;

function applyWatchQrVisibility(handle: string | null): void {
  if (isViewer) return;
  if (!showWatchQr) {
    if (watchQrCtrl) { watchQrCtrl.destroy(); watchQrCtrl = null; }
    return;
  }
  if (watchQrCtrl) {
    watchQrCtrl.update(handle);
  } else {
    watchQrCtrl = mountWatchQrWidget({ handle });
  }
}

async function handleWs(msg: OutMsg): Promise<void> {
  if (msg.type === 'an_round_end') {
    // Run the cascade ON the round scene first, then defer the dispatch so
    // the round scene stays visible until letters land.
    roundEndsAt = 0;
    sfx.reveal();
    try {
      await round.cascadeReveal();
    } catch {
      // ignore — fall through to scene swap.
    }
    await sleep(CASCADE_PAD_MS);
    store.dispatchWs(msg);
    return;
  }
  store.dispatchWs(msg);
  switch (msg.type) {
    case 'an_round_open': {
      sfx.chime();
      const idx = msg.round_index;
      if (idx !== lastRoundIndex) {
        lastRoundIndex = idx;
        // Continuous play — no "FINAL ROUND" label. Keep the splash with
        // round number + category for orientation.
        const title = `ROUND ${idx + 1}`;
        const sub = msg.category.toUpperCase();
        roundSplash.show(title, sub);
      }
      roundEndsAt = msg.ends_at_ms;
      lastRoundTickSecond = -1;
      break;
    }
    case 'an_hint_reveal':
      // sfx.hint() is fired by the round scene when hintLetter changes.
      break;
    case 'an_correct_answer':
      sfx.ding();
      break;
    case 'an_guess_received':
      // Rising chat-bubble overlay (correct OR wrong). Per-tenant rate-limited
      // upstream so the DOM never floods.
      round.spawnGuessBubble(msg.guess);
      break;
    case 'an_session_leaderboard':
      sideLeaderboard.setSessionRows(msg.rows);
      break;
    case 'an_game_over':
      sfx.chime();
      break;
    case 'cross_game_conflict':
      // Banner only matters for the slug being attempted OR the slug currently
      // running — other game scenes shouldn't surface the warning.
      if (msg.attempted_slug === MY_SLUG || msg.running_slug === MY_SLUG) {
        conflictBanner.show(msg.message);
      }
      break;
    case 'state_snapshot': {
      const snapSettings = (msg as any).state?.settings;
      const nextShow = snapSettings && typeof snapSettings.show_watch_qr === 'boolean' ? snapSettings.show_watch_qr : true;
      if (nextShow !== showWatchQr) {
        showWatchQr = nextShow;
        applyWatchQrVisibility(currentHandle);
      }
      break;
    }
    case 'auth_ok' as OutMsg['type']: {
      const authMsg = msg as unknown as { type: 'auth_ok'; handle: string | null };
      currentHandle = authMsg.handle;
      applyWatchQrVisibility(authMsg.handle);
      break;
    }
    default:
      break;
  }
}

store.subscribe((next, prev) => {
  if (next.scene !== prev.scene) applyScene(next.scene);
  if (next.theme && next.theme !== prev.theme) applyTheme(next.theme);
  if (next.scene === 'lobby') {
    lobby.setLeaderboard(next.leaderboard, next.leaderboardAllTime);
  }
  if (next.scene === 'round' && next.round) {
    round.setRound(next.round);
  }
  if (next.scene === 'reveal' && next.reveal) {
    reveal.setReveal(next.reveal);
  }
  if (next.scene === 'podium' && next.podium) {
    podium.setPodium(next.podium);
  }
  // Side leaderboard cycles between all-time + session views internally;
  // we just feed both datasets when they change.
  if (next.leaderboardAllTime !== prev.leaderboardAllTime) {
    sideLeaderboard.setAllTimeRows(next.leaderboardAllTime);
  }
  if (next.sessionLeaderboard !== prev.sessionLeaderboard) {
    sideLeaderboard.setSessionRows(next.sessionLeaderboard);
  }
});

// Drive the round countdown ring at 5Hz while the round scene is visible.
setInterval(() => {
  if (store.getState().scene === 'round') round.tick();
}, 200);

// Tick SFX driver: fires sfx.tick() once per second in the final 5s of the
// round. Backend `an_round_open` populates roundEndsAt; round_end clears it.
setInterval(() => {
  if (roundEndsAt <= 0) return;
  const remainingMs = roundEndsAt - Date.now();
  if (remainingMs <= 0 || remainingMs > 5_000) return;
  const secondsLeft = Math.ceil(remainingMs / 1000);
  if (secondsLeft !== lastRoundTickSecond && secondsLeft >= 1 && secondsLeft <= 5) {
    lastRoundTickSecond = secondsLeft;
    sfx.tick();
  }
}, 200);

function buildMockRow(rank: number, name: string, points: number): LbRow {
  const r: Partial<LbRow> = {};
  r.rank = rank;
  r.player_id = rank;
  r.display_name = name;
  r.avatar_seed = name.toLowerCase();
  r.total_points = points;
  r.games_played = 10;
  r.games_won = rank === 1 ? 4 : 0;
  return r as LbRow;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// --- Connect ---
let wsClient: WsClient | null = null;
function connectWs(): void {
  // Viewer mode → /watch/<handle>/feed (read-only). Streamer mode keeps the
  // existing /browser?key=...&game=anagram handshake.
  const wsUrl = wsUrlFor(viewerMode, resolveWsBase(wsPort), { game: 'anagram' });
  wsClient = new WsClient({
    url: wsUrl,
    sessionToken: key,
    viewer: isViewer,
    onMessage: (m) => {
      void handleWs(m);
    },
    onState: (s) => {
      store.dispatch({ type: 'connection', value: s });
      // The watch SPA owns the connection-lost UI for viewer iframes.
      if (!isViewer) connectionLost.setVisible(s !== 'open');
    },
  });
  wsClient.connect();
  (window as unknown as Record<string, unknown>).__an_ws = wsClient;
}

if (mockMode) {
  const fakeRows: LbRow[] = [
    buildMockRow(1, 'AlphaWolf', 1240),
    buildMockRow(2, 'BetaTester', 980),
    buildMockRow(3, 'GammaRay', 720),
  ];
  lobby.setLeaderboard(fakeRows, fakeRows);
  void audioBus.unlock();
} else {
  connectWs();
}

// Auto-pause when browser source not visible (OBS scene switch / tab bg).
// Closes WS so backend soft-abort grace ends the game when no sources remain
// for this tenant+slug. Reconnects on revisible — FSM re-broadcasts state.
if (!mockMode) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (wsClient) {
        try { wsClient.close(); } catch { /* swallow */ }
        wsClient = null;
      }
    } else if (document.visibilityState === 'visible') {
      if (!wsClient) connectWs();
    }
  });
}

if (!isViewer) {
  window.addEventListener(
    'pointerdown',
    () => {
      void audioBus.unlock();
    },
    { once: true },
  );
}

(window as unknown as Record<string, unknown>).__an_store = store;
// Expose the WS handler so mock harnesses can drive scene-changing flows
// (cascade animation, sparkles) by going through the same path as live WS.
(window as unknown as Record<string, unknown>).__an_handleWs = handleWs;
